import React, { ReactNode } from 'react';
import { OptionProps, components } from 'react-select';
import {
    FlatGroupOptionType,
    flatGroupGetGroupKeyFromCompoundKey,
    flatGroupGetIsGroup,
} from './FlatGroupOptionType';

// generates an id for the label
let drawNumber = 0;

enum CheckboxFieldCheckedStateEnum {
    UNCHECKED,
    CHECKED,
    INDETERMINATE,
}

export enum GroupCheckStateEnum {
    NONE,
    ALL,
    SOME,
}

// determine if all, some or none of the option keys are in a group
export function getCheckStateForGroup(
    values: readonly FlatGroupOptionType[],
    optionKeys: string[]
) {
    const shouldGroupBeIndeterminate =
        !!values.length && optionKeys.some((x) => values.some((y) => y.value === x));

    if (shouldGroupBeIndeterminate) {
        // groups are checked if all members are checked
        const shouldGroupBeChecked =
            !!values.length && optionKeys.every((x) => values.some((y) => y.value === x));

        // if some are checked, set it to indeterminate.
        if (shouldGroupBeChecked) {
            return GroupCheckStateEnum.ALL;
        }
        return GroupCheckStateEnum.SOME;
    }
    return GroupCheckStateEnum.NONE;
}

// surround the matched text with <strong>
function highlightMatch(text: string, match: string): Iterable<ReactNode> {
    const startIndex = text.toLocaleLowerCase().indexOf(match.toLocaleLowerCase());
    const endIndex = startIndex + match.length;
    if (startIndex === -1) {
        return [text];
    }
    const selectedText = text.substring(startIndex, endIndex);
    return [
        text.substring(0, startIndex),
        <strong key={`${startIndex}${selectedText}${endIndex}`}>{selectedText}</strong>,
        text.substring(endIndex),
    ];
}

// keeps track of collapsed groups and returns a custom Option component
export function useReactSelectCollapsibleGroup(
    clickHandler: (operation: 'add' | 'remove', s: string[]) => void
) {
    const [collapsedGroups, setCollapsedGroups] = React.useState<Record<string, boolean>>({});

    const handleGroupToggle = (groupLabel: string) => {
        setCollapsedGroups((prevCollapsedGroups) => ({
            ...prevCollapsedGroups,
            [groupLabel]: !prevCollapsedGroups[groupLabel],
        }));
    };

    /**
     * this is a workaround to force the filter function from GroupSelect
     * to be called when collapsedGroups changes.
     * this should be revisited after upgrading react-select as it should no longer be necessary
     */
    React.useEffect(() => {
        clickHandler('add', []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collapsedGroups]);

    const isGroupCollapsed = (groupLabel: string) => !collapsedGroups[groupLabel];

    function CheckOptionWithGroup(props: OptionProps<FlatGroupOptionType, true>) {
        const isCollapsed = isGroupCollapsed(flatGroupGetGroupKeyFromCompoundKey(props.data.value));
        const isGroup = flatGroupGetIsGroup(props.data);
        const values = props.getValue();
        const cRef = React.useRef<HTMLInputElement>(null);

        // group state to check
        const gstocs = (gs: GroupCheckStateEnum) => {
            switch (gs) {
                case GroupCheckStateEnum.ALL:
                    return CheckboxFieldCheckedStateEnum.CHECKED;
                case GroupCheckStateEnum.SOME:
                    return CheckboxFieldCheckedStateEnum.INDETERMINATE;
                case GroupCheckStateEnum.NONE:
                default:
                    return CheckboxFieldCheckedStateEnum.UNCHECKED;
            }
        };

        let checkState = CheckboxFieldCheckedStateEnum.UNCHECKED;
        if (isGroup) {
            checkState = gstocs(getCheckStateForGroup(values, props.data.optionKeys));
        } else {
            checkState = props.isSelected
                ? CheckboxFieldCheckedStateEnum.CHECKED
                : CheckboxFieldCheckedStateEnum.UNCHECKED;
        }

        React.useEffect(() => {
            if (cRef.current) {
                cRef.current.indeterminate =
                    checkState === CheckboxFieldCheckedStateEnum.INDETERMINATE;
            }
        }, [cRef, checkState]);

        const toggleCheckbox = () => {
            if (isGroup) {
                clickHandler(
                    checkState === CheckboxFieldCheckedStateEnum.UNCHECKED ? 'add' : 'remove',
                    props.data.optionKeys
                );
            }
        };

        drawNumber += 1;
        const id = `input-group-checkbox-${drawNumber}`;
        const userInput = props.selectProps.inputValue || '';
        const label = userInput ? highlightMatch(props.data.label, userInput) : props.data.label;

        return (
            <components.Option
                {...props}
                className={`${props.className ?? ''} ${isGroup ? 'group' : 'option'}`}
            >
                <input
                    ref={cRef}
                    id={id}
                    type="checkbox"
                    onChange={toggleCheckbox}
                    checked={checkState === CheckboxFieldCheckedStateEnum.CHECKED}
                />
                <label htmlFor={id}>{label}</label>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleGroupToggle(props.data.value);
                    }}
                >
                    {isCollapsed ? (
                        <i className="fa-solid fa-chevron-down">
                            <span className="sr-only">Expand</span>
                        </i>
                    ) : (
                        <i className="fa-solid fa-chevron-up">
                            <span className="sr-only">Collapse</span>
                        </i>
                    )}
                </button>
            </components.Option>
        );
    }

    return { CheckOptionWithGroup, isGroupCollapsed };
}
